/**
 * Server-search picker: «choose one record from a list that can grow».
 *
 * The rule it exists for (Standards §"Which lists must be server-paged",
 * rule 3): a picker over a growing list must search against the server, so it
 * never holds a capped list. `search(query)` is the only source of options.
 *
 * v2 (2026-10):
 *  - the list is PORTALLED and positioned by `useAnchoredPosition` (the same
 *    flip + viewport clamp every anchored panel uses), so it is never clipped
 *    by a scrolling table, an `overflow-hidden` card or a modal;
 *  - results can come GROUPED (`{label, options}[]`) or flat, as before;
 *  - keyboard: ↓/↑ move, Enter picks, Esc closes and returns focus, Tab
 *    leaves; the highlighted option is announced with
 *    `aria-activedescendant`, so focus stays in the search box. Enter does
 *    NOTHING while the list on screen does not answer the text in the box
 *    (debounce or request still running): it would pick a record of the old
 *    list and drop what was just typed. «Create new» is never highlighted on
 *    its own — the user moves to it with ↓ — so Enter never opens the create
 *    flow by accident;
 *  - the whole search state machine (which answer is accepted, what is
 *    highlighted, what closing resets) is `lib/selection.ts::pickerReducer`,
 *    pinned by a Node test: an answer that lands after the list closed — or for
 *    an earlier query — is dropped, so a reopened list starts from a spinner;
 *  - no Spanish defaults: `placeholder` and `emptyLabel` are required, and
 *    `createNewLabel` is required exactly when `onCreateNew` is passed.
 *
 * The search box is NOT portalled — when the field opens it turns into the
 * input in place. Focus therefore never leaves the field's own DOM (a modal's
 * focus trap and Tab order keep working); only the passive list floats.
 */
import React, { useEffect, useId, useMemo, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { useAnchoredPosition } from '../hooks/useAnchoredPosition';
import {
    enterPicks,
    pickerInitialState,
    pickerPending,
    pickerReducer,
    type PickerAction,
    type PickerState,
} from '../lib/selection';

export interface EntitySelectorOption<T = unknown> {
    /** Stable identifier (string), UNIQUE across all sections of one answer:
     *  selection and highlight compare ids, so a design and a piece both
     *  numbered 5 must be namespaced by the caller (`design:5`, `piece:5`). */
    id: string;
    /** Display label shown in the list and in the closed field. */
    label: string;
    /** Optional secondary text rendered smaller under the label. */
    subtitle?: string;
    /** Optional leading thumbnail URL. */
    thumbnailUrl?: string | null;
    /** Payload passed back on selection. */
    value: T;
}

/** A titled group of results («Diseños», «Piezas»…). */
export interface EntitySelectorSection<T = unknown> {
    label: string;
    options: EntitySelectorOption<T>[];
}

/** What `search` may return: a flat list (as before) or sections. */
export type EntitySelectorResults<T = unknown> = EntitySelectorOption<T>[] | EntitySelectorSection<T>[];

interface EntitySelectorBaseProps<T> {
    /** The current selection. Pass the hydrated label even when the record is
     *  outside any page the search would return (Standards: a picker cannot
     *  resolve the label of a value it no longer holds). */
    value: EntitySelectorOption<T> | null;
    /** Called when the user picks an option. */
    onChange: (option: EntitySelectorOption<T> | null) => void;
    /** Async search. Receives the current query; returns options or sections.
     *  MEMOISE it (`useCallback`): a new identity re-runs the search while the
     *  list is open — that is how a caller refreshes an open list when its own
     *  data changes (the expense editor's AI-proposed supplier). An inline
     *  function would re-run it after every answer. */
    search: (query: string) => Promise<EntitySelectorResults<T>>;
    /** Visible label above the field (also its accessible name). */
    label?: string;
    /** Text of the closed field with no value, and of the search box. */
    placeholder: string;
    /** Shown when the search returned nothing. */
    emptyLabel: string;
    /** Screen-reader text while a search runs (a spinner shows either way). */
    loadingLabel?: string;
    /** Shown when `search` threw. Default: `emptyLabel`. */
    errorLabel?: string;
    disabled?: boolean;
    /** Min query length before triggering search (default 0 = search on focus). */
    minQueryLength?: number;
    /** Debounce ms (default 250). */
    debounceMs?: number;
    className?: string;
}

export type EntitySelectorCreateProps =
    | { onCreateNew?: undefined; createNewLabel?: undefined }
    | {
          /** Adds a «create new» action at the end of the list (also reachable
           *  with the keyboard, as the last item). Receives what was typed in
           *  the search box, so the create form can start with that name. */
          onCreateNew: (query: string) => void;
          createNewLabel: string;
      };

export type EntitySelectorProps<T = unknown> = EntitySelectorBaseProps<T> & EntitySelectorCreateProps;

function EntitySelector<T = unknown>({
    value,
    onChange,
    search,
    onCreateNew,
    createNewLabel,
    label,
    placeholder,
    emptyLabel,
    loadingLabel,
    errorLabel,
    disabled,
    minQueryLength = 0,
    debounceMs = 250,
    className = '',
}: EntitySelectorProps<T>) {
    type Opt = EntitySelectorOption<T>;
    const [state, dispatch] = useReducer(
        (prev: PickerState<Opt>, action: PickerAction<Opt>) => pickerReducer<Opt>(prev, action),
        undefined,
        () => pickerInitialState<Opt>(),
    );
    const { open, query, loading, failed, sections, activeIndex } = state;

    const fieldRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    /** Request ids only ever grow; the reducer accepts the answer of the one
     *  it awaits and drops every other (an earlier query, or a closed list). */
    const nextRequestRef = useRef(0);
    const valueRef = useRef(value);
    valueRef.current = value;
    const restoreFocusRef = useRef(false);

    const uid = useId();
    const labelId = `${uid}-label`;
    const valueId = `${uid}-value`;
    const listId = `${uid}-list`;
    const optionId = (i: number) => `${uid}-opt-${i}`;

    const flat = useMemo(() => sections.flatMap(s => s.options), [sections]);
    /** The «create new» action is the last keyboard stop, after the options. */
    const itemCount = flat.length + (onCreateNew ? 1 : 0);
    const createIndex = onCreateNew ? flat.length : -1;
    /** Debounce running, request in flight, or results of an earlier query. */
    const pending = pickerPending(state);

    const pos = useAnchoredPosition(fieldRef, open, { estimatedHeight: 300, gap: 4 });

    const close = (refocus: boolean) => {
        // `||=`: a pick closes with refocus, and the input's blur on unmount
        // may call close(false) right after — it must not cancel the refocus.
        restoreFocusRef.current ||= refocus;
        // Resets everything, the awaited request included (pickerReducer).
        dispatch({ type: 'close' });
    };

    // Search when open, when the query changes, or when the caller hands a new
    // `search` (memoised: its data changed). The spinner shows from the first
    // keystroke, not after the debounce.
    useEffect(() => {
        if (!open) return;
        const q = query;
        if (q.length < minQueryLength) {
            dispatch({ type: 'skip', query: q });
            return;
        }
        const id = ++nextRequestRef.current;
        dispatch({ type: 'request', id });
        const timer = window.setTimeout(async () => {
            try {
                const results = await search(q);
                dispatch({ type: 'answer', id, query: q, results, valueId: valueRef.current?.id });
            } catch {
                dispatch({ type: 'fail', id, query: q });
            }
        }, debounceMs);
        return () => window.clearTimeout(timer);
    }, [open, query, search, minQueryLength, debounceMs]);

    useEffect(() => {
        if (!open || activeIndex < 0) return;
        document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, open]);

    // Focus follows the mode: the input when it opens, the trigger when it
    // closes through Esc or a pick (never when the user tabbed away).
    useEffect(() => {
        if (open) {
            inputRef.current?.focus();
        } else if (restoreFocusRef.current) {
            restoreFocusRef.current = false;
            triggerRef.current?.focus();
        }
    }, [open]);

    // Outside pointer: the input's blur covers most cases; this covers a
    // pointer-down that lands nowhere focusable.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            if (fieldRef.current?.contains(target) || panelRef.current?.contains(target)) return;
            close(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const openList = () => {
        if (disabled) return;
        dispatch({ type: 'open' });
    };

    const pick = (opt: EntitySelectorOption<T>) => {
        onChange(opt);
        close(true);
    };

    const runCreate = () => {
        if (!onCreateNew) return;
        // Read before closing: closing resets the search box.
        const typed = query.trim();
        close(false);
        onCreateNew(typed);
    };

    const activate = (index: number) => {
        if (index === createIndex) runCreate();
        else {
            const opt = flat[index];
            if (opt) pick(opt);
        }
    };

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                dispatch({ type: 'move', delta: 1, itemCount });
                break;
            case 'ArrowUp':
                e.preventDefault();
                dispatch({ type: 'move', delta: -1, itemCount });
                break;
            case 'Enter':
                // Never submit the surrounding form from the search box — and
                // never act on a list that does not answer what is typed.
                e.preventDefault();
                if (enterPicks(activeIndex, pending)) activate(activeIndex);
                break;
            case 'Escape':
                // Close the list only — a surrounding modal must not close too.
                e.preventDefault();
                e.stopPropagation();
                close(true);
                break;
            default:
                break;
        }
    };

    const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            openList();
        }
    };

    const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const next = e.relatedTarget as Node | null;
        if (next && (fieldRef.current?.contains(next) || panelRef.current?.contains(next))) return;
        close(false);
    };

    const accessibleName = label ?? placeholder;
    const showLoadingRow = pending && flat.length === 0;
    const showEmptyRow = !pending && flat.length === 0;
    let flatIndex = -1;

    const renderOption = (opt: EntitySelectorOption<T>) => {
        flatIndex += 1;
        const idx = flatIndex;
        const selected = value?.id === opt.id;
        const active = idx === activeIndex;
        return (
            <div
                key={opt.id}
                id={optionId(idx)}
                role="option"
                aria-selected={selected}
                onClick={() => pick(opt)}
                onMouseMove={() => dispatch({ type: 'hover', index: idx })}
                className={`flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                    // An old list (a newer search is running) stays clickable
                    // but reads as provisional.
                    pending ? 'opacity-60' : ''
                } ${
                    selected
                        ? 'bg-primary/10 text-primary font-medium'
                        : active
                            ? 'bg-slate-100 dark:bg-surface-hover-dark text-text-main dark:text-white'
                            : 'text-text-main dark:text-white'
                }`}
            >
                {opt.thumbnailUrl && (
                    <img src={opt.thumbnailUrl} alt="" className="h-7 w-7 shrink-0 rounded object-cover" />
                )}
                <span className="min-w-0 flex-1">
                    <span className="block truncate">{opt.label}</span>
                    {opt.subtitle && (
                        <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">{opt.subtitle}</span>
                    )}
                </span>
                {selected && <Icon name="check" className="!text-[16px] shrink-0 text-primary" />}
            </div>
        );
    };

    return (
        <div className={`relative ${className}`}>
            {label && (
                <span
                    id={labelId}
                    className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1"
                >
                    {label}
                </span>
            )}
            <div ref={fieldRef}>
                {open ? (
                    <div
                        // A press on the field around the input (the magnifier,
                        // the spinner, the padding) would blur the input and
                        // close the list the user is typing in: keep the focus.
                        onMouseDown={e => { if (e.target !== inputRef.current) e.preventDefault(); }}
                        className="flex h-10 w-full items-center gap-2 rounded-md border border-primary bg-white px-3 ring-1 ring-primary dark:bg-surface-dark"
                    >
                        <Icon name="search" className="!text-[18px] shrink-0 text-slate-400" />
                        <input
                            ref={inputRef}
                            type="text"
                            role="combobox"
                            aria-expanded
                            aria-controls={listId}
                            aria-autocomplete="list"
                            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
                            aria-labelledby={label ? labelId : undefined}
                            aria-label={label ? undefined : accessibleName}
                            value={query}
                            onChange={e => dispatch({ type: 'type', query: e.target.value })}
                            onKeyDown={onInputKeyDown}
                            onBlur={onInputBlur}
                            placeholder={placeholder}
                            autoComplete="off"
                            className="min-w-0 flex-1 bg-transparent text-sm text-text-main outline-none placeholder:text-slate-400 dark:text-white"
                        />
                        {loading && (
                            <Icon name="progress_activity" className="!text-[16px] shrink-0 animate-spin text-slate-400" />
                        )}
                    </div>
                ) : (
                    <button
                        ref={triggerRef}
                        type="button"
                        onClick={openList}
                        onKeyDown={onTriggerKeyDown}
                        disabled={disabled}
                        aria-haspopup="listbox"
                        aria-expanded={false}
                        aria-labelledby={label ? `${labelId} ${valueId}` : undefined}
                        aria-label={label ? undefined : value ? `${accessibleName}: ${value.label}` : accessibleName}
                        className={`w-full h-10 px-3 flex items-center justify-between gap-2 rounded-md border border-border-strong dark:border-border-strong-dark bg-white dark:bg-surface-dark text-left text-sm text-text-main dark:text-white transition-colors ${
                            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/60'
                        }`}
                    >
                        {value ? (
                            <span id={valueId} className="flex items-center gap-2 min-w-0 flex-1">
                                {value.thumbnailUrl && (
                                    <img src={value.thumbnailUrl} alt="" className="w-6 h-6 rounded object-cover shrink-0" />
                                )}
                                <span className="truncate">{value.label}</span>
                            </span>
                        ) : (
                            <span id={valueId} className="text-slate-400">{placeholder}</span>
                        )}
                        <Icon name="expand_more" className="!text-[18px] text-slate-400" />
                    </button>
                )}
            </div>

            {open && pos && createPortal(
                <div
                    ref={panelRef}
                    data-dropdown-portal=""
                    // Keep focus in the search box while clicking an option or
                    // scrolling the list (a blur would close it first).
                    onMouseDown={e => e.preventDefault()}
                    style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width }}
                    className="z-[200] overflow-hidden rounded-xl border border-border-subtle bg-white shadow-xl dark:border-border-subtle-dark dark:bg-surface-dark"
                >
                    <div
                        id={listId}
                        role="listbox"
                        aria-label={accessibleName}
                        aria-busy={loading || undefined}
                        className="max-h-60 overflow-y-auto p-1.5"
                    >
                        {showLoadingRow && (
                            <div role="presentation" className="flex items-center justify-center gap-2 px-3 py-4 text-xs text-slate-400">
                                <Icon name="progress_activity" className="!text-[16px] animate-spin" />
                                {loadingLabel && <span className="sr-only">{loadingLabel}</span>}
                            </div>
                        )}
                        {showEmptyRow && (
                            <div role="presentation" className="px-3 py-4 text-center text-xs text-slate-400">
                                {failed ? (errorLabel ?? emptyLabel) : emptyLabel}
                            </div>
                        )}
                        {sections.map((section, si) =>
                            section.label ? (
                                <div key={`${si}:${section.label}`} role="group" aria-labelledby={`${uid}-sec-${si}`}>
                                    <div
                                        id={`${uid}-sec-${si}`}
                                        role="presentation"
                                        className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 select-none"
                                    >
                                        {section.label}
                                    </div>
                                    {section.options.map(renderOption)}
                                </div>
                            ) : (
                                <React.Fragment key={`${si}:`}>{section.options.map(renderOption)}</React.Fragment>
                            ),
                        )}
                        {onCreateNew && (
                            <div
                                id={optionId(createIndex)}
                                role="option"
                                aria-selected={false}
                                onClick={runCreate}
                                onMouseMove={() => dispatch({ type: 'hover', index: createIndex })}
                                className={`mt-1 flex cursor-pointer items-center gap-1 rounded-lg border-t border-border-subtle px-3 py-2 text-xs font-bold text-primary dark:border-border-subtle-dark ${
                                    activeIndex === createIndex ? 'bg-slate-100 dark:bg-surface-hover-dark' : ''
                                }`}
                            >
                                <Icon name="add" className="!text-[16px]" />
                                {createNewLabel}
                            </div>
                        )}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}

export default EntitySelector;
